import {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  useId,
} from 'react';
import { useClientLocale } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { getInputStyleByAmountLength } from './getInputStyleByAmountLength';

type AmountInputProps = {
  onChange: ChangeEventHandler<HTMLInputElement>;
  onBlur?: (value: string) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  value: string;
  placeholder?: HTMLInputElement['placeholder'];
  disabled?: HTMLInputElement['disabled'];
  smaller?: boolean;
};

export const amountInputWrapperStyles = css({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  '& .inputHeight': {
    height: '80px',
  },
  '& .cursorPointer': {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '& .placeHolderFiller': {
    color: 'neutrals.fill_quinary',
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& input': {
    width: '100%',
    height: '80px',
    color: 'neutrals.text_primary',
    caretColor: 'neutrals.text_primary',
    '&::placeholder': {
      color: 'neutrals.fill_quinary',
    },
    _focus: {
      border: 'none',
      outline: 'none',
    },
    _disabled: {
      background: 'none',
    },
  },
});

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (
    { onBlur, onChange, onFocus, value, placeholder, disabled, smaller },
    ref,
  ) => {
    const locale = useClientLocale();

    const fontSizeStyles = getInputStyleByAmountLength(value, smaller);

    return (
      <div className={amountInputWrapperStyles}>
        <input
          autoComplete="off"
          aria-label="amount input field"
          data-testid="special-input-field__input"
          disabled={disabled}
          ref={ref}
          data-element="input"
          placeholder={placeholder}
          id={`amount-${useId()}`}
          type="text"
          inputMode="decimal"
          value={value}
          lang={locale}
          onChange={onChange}
          onBlur={(e) => onBlur?.(e.target.value)}
          onFocus={onFocus}
          className="inputHeight"
          style={fontSizeStyles}
        />
      </div>
    );
  },
);
